/**
 * @fileoverview Logic for row cells rendering
 */
import React from 'react'
import moment from 'moment'

import { StatusLabel } from '../../../../../Components/StatusLabel'
import Checkbox from '@material-ui/core/Checkbox'
import {
  formatFieldValue,
  roundingAfterDecimalPoint,
} from '../../../../../Utils/Formatters'
import MissionTableActions from '../../../../../Components/MissionTableActions/MissionTableActions'
import {
  MISSION_STATUS_ENUM,
  MISSION_STATUS_ENUM_MAP,
} from '../../../../../shared/constants'
import { dropTimeZoneFromDate } from '../../../../../shared/libraries/utils/date'
import { getMissionDisplayStatus } from '../../../../../shared/missionReadyToPublish'
import { DATETIME_PICKER_FORMAT } from '../../../../../constants'
/**
 * Function that formats data for each table column and return object that will be rendered into row by SimpleTableTable component
 *
 * @param {number} props.id Mission's id, used to track whether row's checkbox is checked and passed to onCheckboxChange function
 * @param {function} props.onCheckboxChange Called on checkbox click
 * @param {boolean} props.isChecked Indicates whether row is checked
 * @param {string} props.status Mission's status
 * @param {boolean} props.isReadyToPublish Whether the draft mission is signed off
 * @param {number} props.density Mission's area density (per 1km^2)
 * @param {number} props.addresses Mission's area addresses amount
 * @param {date} props.week Mission's week
 *
 * @returns {object} Object that will be rendered into row in the table
 *
 * @example letterboxMissionsTableRowRenderer({
 *  id: 53454,
 *  onCheckboxChange: () => {},
 *  isChecked: true,
 *  status: 'AVAILABLE',
 *  density: 23.5,
 *  addresses: 494,
 *  week: ISODATE,
 * })
 */
const letterboxMissionsTableRowRenderer = ({
  id,
  label,
  cancelled,
  onCheckboxChange,
  isChecked,
  status,
  isReadyToPublish,
  density,
  addresses,
  flyerCode,
  packSize,
  totalFlyersDistributed,
  payAdjustment,
  bonusPerConversion,
  bonusThreshold,
  costPerFlyer,
  week,
  baName,
  baId,
  // Unfortuntely needed for the link to BA route
  // TODO Refactor the BA route to not need a city id
  cityId,
  onDelete,
  dateTime,
  meetingPoint,
  onEditMeetingPoint,
  boundary,
}) => {
  return {
    'Select': (
      <Checkbox
        defaultChecked={true}
        checked={isChecked}
        onChange={() => onCheckboxChange(id)}
      />
    ),
    'Label': formatFieldValue(label),
    'Status': (
      <StatusLabel
        status={
          cancelled && status != MISSION_STATUS_ENUM.CANCELLED
            ? `${MISSION_STATUS_ENUM.CANCELLED} / ${status}`
            : getMissionDisplayStatus({ status, isReadyToPublish })
        }
      />
    ),
    'Density': formatFieldValue(density),
    'Flyer Code': flyerCode,
    'Pack Size': packSize,
    'Flyers Distributed': totalFlyersDistributed,
    'Pay Adjustment': payAdjustment,
    'Bonus per conversion': bonusPerConversion || '-',
    'Bonus threshold': bonusThreshold || '-',
    'Cost Per Flyer': roundingAfterDecimalPoint('ceil', costPerFlyer, 3),
    'Addresses': formatFieldValue(addresses),
    'Meeting Point': meetingPoint ? (
      <div style={{ minWidth: 240, paddingTop: 10, paddingBottom: 10 }}>
        <div
          style={{
            fontFamily: '"Roboto Regular", Helvetica, Arial, sans-serif',
            fontSize: 14,
          }}
        >
          {meetingPoint.address}
        </div>
        <div
          style={{
            fontFamily: '"Roboto Regular", Helvetica, Arial, sans-serif',
            fontSize: 14,
          }}
        >
          {meetingPoint.dateTime
            ? moment.utc(meetingPoint.dateTime).format(DATETIME_PICKER_FORMAT)
            : '-'}
        </div>
      </div>
    ) : (
      '-'
    ),
    'Week': week,
    'Date': moment(dropTimeZoneFromDate(dateTime)).format(
      DATETIME_PICKER_FORMAT,
    ),
    'BA': baName ? (
      <a
        href={`/dashboard/cities/${cityId}/ambassadors/${baId}`}
        target="_blank"
        rel="noreferrer"
      >
        {baName}
      </a>
    ) : (
      '-'
    ),
    'Actions': (
      <MissionTableActions
        missionId={id}
        statusNumber={MISSION_STATUS_ENUM_MAP[status]}
        onDelete={onDelete}
        onEditMeetingPoint={onEditMeetingPoint}
        meetingPoint={meetingPoint}
        mission={{
          id,
          dateTime,
          meetingPoint,
          status,
          boundary,
          label: formatFieldValue(label),
        }}
      />
    ),
  }
}

export default letterboxMissionsTableRowRenderer
